import { getErrorMessage } from "./error";

/**
 * A Sui Move abort in `module` with `code`. The code surfaces either as the node's effects string
 * (`..., <code>) in command N`) or as a resolved error (`abort code: <code>,`); this mirrors the
 * check in Circle's own Sui tests (circlefin/sui-cctp scripts/test/helpers/expectAbort.ts).
 */
function isSuiMoveAbort(message: string, module: string, code: number): boolean {
  return (
    namesModule(message, module) &&
    (message.includes(`, ${code})`) || message.includes(`abort code: ${code},`))
  );
}

const IDENTIFIER_CHAR = /[A-Za-z0-9_]/;

/**
 * Whether `message` names `module` on its own: not preceded by an identifier character, so
 * receive_message can't match inside handle_receive_message, and followed by its quote (effects)
 * or `::` (resolved).
 */
function namesModule(message: string, module: string): boolean {
  for (let at = message.indexOf(module); at !== -1; at = message.indexOf(module, at + 1)) {
    const before = message[at - 1];
    const after = message.slice(at + module.length);
    if (
      (!before || !IDENTIFIER_CHAR.test(before)) &&
      (after[0] === '"' || after.startsWith("::"))
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Detect a claim of a CCTP message that has already been claimed (nonce consumed).
 * Aptos aborts with ENONCE_ALREADY_USED: error::already_exists(10) = 0x8000a in v1 and
 * error::already_exists(7) = 0x80007 in v2. Solana v1 raises NonceAlreadyUsed; EVM reverts
 * "Nonce already used" in both versions. Sui v2's receive_message aborts with ENonceAlreadyUsed = 4.
 */
export function isCCTPNonceUsedError(error: unknown): boolean {
  const message = getErrorMessage(error) ?? "";
  return (
    message.includes("ENONCE_ALREADY_USED") ||
    (message.includes("message_transmitter") && /\b0x8000[a7]\b/i.test(message)) ||
    message.includes("NonceAlreadyUsed") ||
    message.includes("Nonce already used") ||
    isSuiMoveAbort(message, "receive_message", 4)
  );
}

/**
 * Detect a claim of a v2 Fast Transfer whose attestation passed its `expirationBlock`; Circle must
 * re-attest it before it can be claimed. Aptos aborts with EEXPIRED_MESSAGE:
 * error::invalid_argument(12) = 0x1000c. Sui's prepare_mint aborts with EExpiredMessage = 8.
 */
export function isCCTPMessageExpiredError(error: unknown): boolean {
  const message = getErrorMessage(error) ?? "";
  return (
    message.includes("EEXPIRED_MESSAGE") ||
    (message.includes("token_messenger_minter") && /\b0x1000c\b/i.test(message)) ||
    message.includes("Message expired and must be re-signed") ||
    message.includes("MessageExpired") ||
    isSuiMoveAbort(message, "handle_receive_message", 8)
  );
}

/**
 * Detect the fullnode's INVALID_SIGNATURE validation rejection (vm_error_code 1),
 * surfaced through the gas station's 400 response body. Validation happens
 * before execution, so this error guarantees nothing was committed on-chain.
 * Seen when MPC wallets (e.g. Fordefi) sign the AIP-52 fee-payer envelope
 * incorrectly.
 */
export function isInvalidSignatureError(error: unknown): boolean {
  return /INVALID_SIGNATURE/i.test(getErrorMessage(error) ?? "");
}

/**
 * Detect the gas station rejecting a request because the API key's application
 * has no fee-payer account. The gas station refuses before signing as fee payer,
 * so nothing reaches the fullnode.
 */
export function isFeePayerNotFoundError(error: unknown): boolean {
  return /fee payer not found/i.test(getErrorMessage(error) ?? "");
}

/**
 * Detect wallet transaction rejection errors from EVM and Aptos wallets.
 *
 * Common indicators:
 * - ethers v6: code "ACTION_REJECTED", message "user rejected action"
 * - EIP-1193: error code 4001
 * - MetaMask: "User denied transaction signature"
 * - Aptos wallet adapter: "User has rejected the request"
 * - Generic: "user rejected", "user denied", "rejected by user"
 */
export function isWalletRejectionError(error: unknown): boolean {
  if (!error) return false;

  const message = getErrorMessage(error)?.toLowerCase() ?? "";

  if (
    message.includes("user rejected") ||
    message.includes("user denied") ||
    message.includes("rejected by user") ||
    message.includes("rejected the request") ||
    message.includes("user cancelled") ||
    message.includes("user canceled")
  ) {
    return true;
  }

  if (typeof error === "object" && "code" in error) {
    const code = error.code;
    if (code === "ACTION_REJECTED" || code === 4001) return true;
  }

  return false;
}

/** Map of chain name to native gas token symbol */
const NATIVE_GAS_TOKENS: Record<string, string> = {
  Solana: "SOL",
  Ethereum: "ETH",
  Arbitrum: "ETH",
  Base: "ETH",
  Sui: "SUI",
};

/** Get the native gas token symbol for a given chain (e.g., "SOL" for "Solana") */
export function getNativeGasToken(chain: string): string | undefined {
  return NATIVE_GAS_TOKENS[chain];
}

/**
 * Detect insufficient native gas token errors from cross-chain bridge transactions.
 *
 * Each chain's SDK/RPC throws different error messages when the user lacks gas:
 * - Solana: "AccountNotFound" (account doesn't exist without SOL),
 *           "insufficient lamports", "insufficient funds for rent"
 * - EVM (Ethereum/Arbitrum/Base): "insufficient funds",
 *           "gas required exceeds", "cannot estimate gas"
 * - Sui: "InsufficientGas", "unable to pay"
 */
export function isInsufficientGasError(error: unknown): boolean {
  if (!error) return false;

  const message = getErrorMessage(error)?.toLowerCase() ?? "";

  return (
    message.includes("accountnotfound") ||
    message.includes("insufficient lamports") ||
    message.includes("insufficient funds for rent") ||
    message.includes("insufficientfundsforrent") ||
    message.includes("insufficient funds") ||
    message.includes("gas required exceeds") ||
    message.includes("cannot estimate gas") ||
    message.includes("insufficientgas") ||
    message.includes("unable to pay")
  );
}

/**
 * Classification of USDC → USDCBL atomic-swap failures. Every deposit triggers this swap
 * on the Aptos side via `stablecoin_wrapper::contribute_stable`, which in turn calls the
 * external Bridge.xyz `stablecoin_gateway`. Aborts from either module surface as Move
 * abort errors in the deposit transaction.
 */
export type SwapErrorKind =
  | "liquidity"
  | "invalidAmount"
  | "limitExceeded"
  | "unauthorized"
  | "configError";

const GATEWAY_ERROR_MAP: Record<string, SwapErrorKind> = {
  einvalidamount: "invalidAmount",
  etransactionlimitexceeded: "limitExceeded",
  eunauthorizedcaller: "unauthorized",
  einvalidtokenaddress: "configError",
  einvalidfeebps: "configError",
  ezeroaddress: "configError",
  einvalidtokendecimals: "configError",
  einvalidauthpolicy: "configError",
  esameinputandoutputtoken: "configError",
};

/**
 * Classify a deposit transaction error as a stablecoin-swap failure, or `null` if it's
 * something else (wallet rejection, gas, generic RPC error).
 *
 * Matches on the module name — `stablecoin_wrapper` (Decibel-owned) or `stablecoin_gateway`
 * (Bridge.xyz) — so deployment addresses can vary across mainnet/testnet.
 *
 * Handles both abort formats emitted by the Aptos VM:
 * - Named: `Move abort in 0x...::stablecoin_gateway: EUnauthorizedCaller(0x5): ...`
 * - Raw hex: `Move abort in 0x...::stablecoin_wrapper: 0x4`
 */
export function classifyStablecoinSwapError(error: unknown): SwapErrorKind | null {
  if (!error) return null;
  const lower = getErrorMessage(error)?.toLowerCase() ?? "";

  if (lower.includes("::stablecoin_gateway")) {
    const nameMatch = /::stablecoin_gateway[^a-z0-9]*:?\s*(e[a-z0-9_]+)/.exec(lower);
    const errorName = nameMatch?.[1];
    const kind = errorName ? GATEWAY_ERROR_MAP[errorName] : undefined;
    return kind ?? "configError";
  }

  if (lower.includes("::stablecoin_wrapper")) {
    if (/\beinvalid_wrapper_balance\b/.test(lower) || /:\s*0x0*4\b/.test(lower)) {
      return "liquidity";
    }
    if (/\beinvalid_amount\b/.test(lower) || /:\s*0x0*5\b/.test(lower)) {
      return "invalidAmount";
    }
    return "configError";
  }

  return null;
}

/**
 * Detect EVM gas pricing errors where the transaction's max fee per gas
 * is lower than the current block base fee.
 *
 * This happens when the network base fee rises between gas estimation and
 * transaction submission. Common on L2s like Arbitrum during congestion.
 */
export function isGasPriceTooLowError(error: unknown): boolean {
  if (!error) return false;

  const message = getErrorMessage(error)?.toLowerCase() ?? "";

  return (
    message.includes("max fee per gas less than block base fee") ||
    message.includes("transaction underpriced") ||
    message.includes("replacement transaction underpriced") ||
    message.includes("max priority fee per gas higher than max fee per gas") ||
    (message.includes("maxfeepergas") && message.includes("less than"))
  );
}
